/**
 * Breadcrumbs — auto-derived from the URL path against a friendly-label map.
 *
 * Ported from QYAS Analytics. The QYAS name map covered buy-side trading
 * (FoF, performance, attribution, etc.) — this version is qfinpricer-flavored
 * (alm, risk, instruments, market-data, reference-data, orchestrator, wiki).
 *
 * Spec: 2026-05-19-qfinpricer-portal-skin-qyas.md (Day 1, item 1)
 */

import React from 'react';
import { useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Breadcrumbs as MuiBreadcrumbs,
  Link,
  Typography,
  Box,
} from '@mui/material';
import { ChevronRight } from 'lucide-react';

const NAME_MAP: Record<string, string> = {
  // Module roots
  'alm': 'BSM & Risk Mngt',
  'reference-data': 'Reference Data',
  'market-data': 'Market Data',
  'instruments': 'Instruments',
  'risk': 'Risk Analytics',
  'portfolio': 'Portfolio',
  'orchestrator': 'Orchestrator',
  'wiki': 'Knowledge Wiki',

  // Common admin segments
  'admin': 'Admin',
  'new': 'Create',
  'edit': 'Edit',
  'detail': 'Details',

  // ALM analytics
  'eve-analysis': 'EVE Analysis',
  'lcr-dashboard': 'LCR Dashboard',
  'nsfr-dashboard': 'NSFR Dashboard',
  'gap-analysis': 'Gap Analysis',
  'position-upload': 'Position Upload',
  'liquidity-ratio': 'Liquidity Ratios',
  'lcr-runoff-rate': 'LCR Runoff Rates',
  'liquidity-cashflow': 'Liquidity Cashflows',
  'eve-simulation': 'EVE Simulations',
  'nii-simulation': 'NII Simulations',
  'behavioral-model': 'Behavioral Models',
  'stress-scenario-alm': 'Stress Scenarios',
  'monte-carlo-simulation': 'Monte Carlo',
  'alm-position': 'BSM Positions',
  'ftp-rate': 'FTP Rates',
  'ftp-allocation': 'FTP Allocations',
  'reconciliation-result': 'Reconciliation',
  'audit-trail': 'Audit Trail',
  'tolerance-rule': 'Tolerance Rules',
  'basel-capital-requirement': 'Basel Capital',
  'rwa-calculation': 'RWA Calculations',

  // Market data
  'curves': 'Curves',
  'vol': 'Volatility',
  'curve-definitions': 'Curve Definitions',
  'curve-snapshots': 'Curve Snapshots',
  'curve-points': 'Curve Points',
  'curve-instruments': 'Curve Instruments',
  'vol-surfaces': 'Vol Surfaces',
  'vol-surface-snapshots': 'Vol Surface Snapshots',
  'fixings': 'Fixings',
  'fx-spots': 'FX Spots',
  'credit-spread-curves': 'Credit Curves',

  // Reference data
  'calendars': 'Calendars',
  'currencies': 'Currencies',
  'currency-pairs': 'Currency Pairs',
  'interest-rate-indexes': 'Interest Rate Indexes',
  'day-count-conventions': 'Day-Count Conventions',
  'business-day-conventions': 'Business-Day Conventions',
  'frequencies': 'Frequencies',
  'holidays': 'Holidays',

  // Instruments
  'swaps': 'Swaps',
  'booking': 'Booking',
  'bonds': 'Bonds',
  'options': 'Options',
  'fras': 'FRAs',
  'cds': 'CDS',
  'portfolios': 'Portfolios',
  'trades': 'Trades',

  // Risk
  'pricing-requests': 'Pricing Requests',
  'greeks': 'Greeks',
  'scenarios': 'Scenarios',
  'stress-tests': 'Stress Tests',
  'sensitivity-grids': 'Sensitivity Grids',
  'calibration': 'Calibration',

  // Orchestrator
  'dashboard': 'Dashboard',
  'agents': 'Agents',
  'agent-events': 'Agent Events',
  'agent-invocations': 'Invocations',
  'orchestrator-chats': 'Chats',

  // Wiki
  'pages': 'Pages',
  'links': 'Links',
  'logs': 'Logs',
  'sources': 'Sources',
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function prettify(seg: string): string {
  if (UUID_RE.test(seg)) return 'Details';
  if (/^\d+$/.test(seg)) return `#${seg}`;
  if (NAME_MAP[seg]) return NAME_MAP[seg];
  // Fallback: title-case kebab/snake
  return seg
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

export const Breadcrumbs: React.FC = () => {
  const location = useLocation();
  const segments = location.pathname.split('/').filter(Boolean);

  // Suppress on the home page.
  if (segments.length === 0) return null;

  return (
    <Box sx={{ mb: 2 }}>
      <MuiBreadcrumbs separator={<ChevronRight size={14} />}>
        <Link component={RouterLink} to="/" underline="hover" color="inherit" fontSize={13}>
          Home
        </Link>
        {segments.map((seg, idx) => {
          const isLast = idx === segments.length - 1;
          const to = '/' + segments.slice(0, idx + 1).join('/');
          const label = prettify(seg);

          return isLast ? (
            <Typography color="text.primary" fontSize={13} key={to}>
              {label}
            </Typography>
          ) : (
            <Link
              component={RouterLink}
              to={to}
              underline="hover"
              color="inherit"
              fontSize={13}
              key={to}
            >
              {label}
            </Link>
          );
        })}
      </MuiBreadcrumbs>
    </Box>
  );
};

export default Breadcrumbs;
